import { useLazyQuery } from "@apollo/client";

import { useEffect, useMemo } from "react";

import LoadingAnimation from "@/components/loading-animation";
import type { FacetItemType } from "@/components/pagination";
import useAuth from "@/hooks/use-auth";
import usePageLoading from "@/hooks/use-page-loading";

import Badge from "../components/badge";
import CategoriesFilter from "../components/categories-filter";
import Layout from "../layout";
import { QUERY } from "../profile/graphql";
import type {
  BadgeType,
  UserDataType,
  UserType,
  UserVariablesType,
} from "../types";

export interface BadgesProps {
  badges: BadgeType[];
  category?: string;
  facets: FacetItemType[];
  title: string;
  user: UserType;
}

export default function Badges({
  badges,
  category,
  facets,
  user: serverUser,
}: BadgesProps) {
  const { isUser, queryContext } = useAuth();
  const [fetchUser, { data }] = useLazyQuery<UserDataType, UserVariablesType>(
    QUERY
  );
  useEffect(() => {
    if (isUser) {
      fetchUser(queryContext({ isUser: true, username: serverUser.username }));
    }
  }, [serverUser, isUser, queryContext, fetchUser]);

  const user = useMemo(() => data?.user || serverUser, [data, serverUser]);
  const count = useMemo(
    () => facets.reduce((accum, facet) => accum + facet.count, 0),
    [facets]
  );

  const pageLoading = usePageLoading();

  return (
    <Layout title="Badges" user={user}>
      <h1 className="mb-5 hidden font-medium text-18/[1.5em] text-white md:block">
        Badges
      </h1>
      <CategoriesFilter
        count={count}
        facets={facets}
        selectedCategory={category}
        urlPrefix={`/@${serverUser.username}/badges`}
      />
      {pageLoading ? (
        <LoadingAnimation />
      ) : (
        <div className="mt-5 grid grid-cols-4 gap-x-4.5 gap-y-5 md:grid-cols-6 md:gap-x-8">
          {badges.map((badge) => (
            <Badge badge={badge} key={`${badge.id}-${badge.earnedAt}`} />
          ))}
        </div>
      )}
    </Layout>
  );
}
